// ============================================================================
// Drop this into your main router (e.g. App.js), alongside your existing
// routes. Login stays exactly where it already is — the SAME <AccountForm />
// and <LogIn /> pages used by SuperAdmin/Teacher/Student/Parent. LogIn.jsx
// just gets one added branch: if the decoded Token2 role is "SuperAdmin",
// redirect to /admin/tenants instead of a role-specific dashboard.
// See CHANGES_TO_EXISTING_PAGES.md for that diff.
// ============================================================================
import { Route } from 'react-router-dom';

import AdminLayout from './AdminLayout';

import TenantList from './pages/TenantList';
import TenantOnboardWizard from './pages/TenantOnboardWizard';
import TenantDetail from './pages/TenantDetail';
import AdminCredentials from './pages/AdminCredentials';
import Permissions from './pages/Permissions';
import OrganizationProfile from './pages/OrganizationProfile';
import SeedManagement from './pages/SeedManagement';
import ModuleManagement from './pages/Modulemanagement';
import FormManagement from './pages/Formmanagement';
import DbSizesMonitoring from './pages/DbSizesMonitoring';
import AllDatabases from './pages/AllDatabases';
import LoginHistoryLogs from './pages/LoginHistoryLogs';
import AuditLogs from './pages/AuditLogs';
import ExceptionLogs from './pages/ExceptionLogs';
import LoginDashboard from './pages/LoginDashboard';

// Usage inside your <Routes> ... </Routes> block, next to your existing
// /dashboard, /studentdashboard, /teachersdashboard, /ParentDashboard routes:
//
// Note the SAME seven components (AdminCredentials, Permissions,
// OrganizationProfile, SeedManagement, ModuleManagement, FormManagement) are
// mounted TWICE below:
//   - under ":clientCode/..."  -> opens pre-loaded (linked from Tenant Detail)
//   - under "tools/..."        -> opens with a clientCode lookup box first
//                                 (this is what the sidebar links to)
// This is intentional and matches the pattern your log pages already use.
//
// NOTE on the split: SeedManagement covers the DOCUMENTED Step 2 (bulk
// seed/reset of forms+modules via /v1/admin/tenants/seed). ModuleManagement /
// FormManagement cover per-item CRUD via the separate /api/ModuleForm/* API
// (confirmed against Swagger for Create/Update/Delete/Get* — payload shapes
// match exactly). Still unresolved: how organisationId/branchId are resolved
// per tenant — see the note above getModules() in tenantApi.js.
//
// <Route path="/admin/tenants" element={<AdminLayout />}>
//   <Route index element={<TenantList />} />
//   <Route path="new" element={<TenantOnboardWizard />} />
//   <Route path=":clientCode" element={<TenantDetail />} />
//   <Route path=":clientCode/admin-user" element={<AdminCredentials />} />
//   <Route path=":clientCode/permissions" element={<Permissions />} />
//   <Route path=":clientCode/organization" element={<OrganizationProfile />} />
//   <Route path=":clientCode/seed" element={<SeedManagement />} />
//   <Route path=":clientCode/modules" element={<ModuleManagement />} />
//   <Route path=":clientCode/forms" element={<FormManagement />} />
//   <Route path="tools/admin-user" element={<AdminCredentials />} />
//   <Route path="tools/permissions" element={<Permissions />} />
//   <Route path="tools/organization" element={<OrganizationProfile />} />
//   <Route path="tools/seed" element={<SeedManagement />} />
//   <Route path="tools/modules" element={<ModuleManagement />} />
//   <Route path="tools/forms" element={<FormManagement />} />
//   <Route path="monitoring/db-sizes" element={<DbSizesMonitoring />} />
//   <Route path="monitoring/all-databases" element={<AllDatabases />} />
//   <Route path="logs/login-history" element={<LoginHistoryLogs />} />
//   <Route path="logs/audit" element={<AuditLogs />} />
//   <Route path="logs/exceptions" element={<ExceptionLogs />} />
// </Route>

export const RootAdminRoutes = (
  <Route path="/admin/tenants" element={<AdminLayout />}>
    <Route index element={<TenantList />} />
    <Route path="new" element={<TenantOnboardWizard />} />
    <Route path=":clientCode" element={<TenantDetail />} />
    <Route path=":clientCode/admin-user" element={<AdminCredentials />} />
    <Route path=":clientCode/permissions" element={<Permissions />} />
    <Route path=":clientCode/organization" element={<OrganizationProfile />} />
    <Route path=":clientCode/seed" element={<SeedManagement />} />
    <Route path=":clientCode/modules" element={<ModuleManagement />} />
    <Route path=":clientCode/forms" element={<FormManagement />} />
    <Route path="tools/admin-user" element={<AdminCredentials />} />
    <Route path="tools/permissions" element={<Permissions />} />
    <Route path="tools/organization" element={<OrganizationProfile />} />
    <Route path="tools/seed" element={<SeedManagement />} />
    <Route path="tools/modules" element={<ModuleManagement />} />
    <Route path="tools/forms" element={<FormManagement />} />
    <Route path="monitoring/db-sizes" element={<DbSizesMonitoring />} />
    <Route path="monitoring/all-databases" element={<AllDatabases />} />
    <Route path="logs/login-history" element={<LoginHistoryLogs />} />
    <Route path="logs/audit" element={<AuditLogs />} />
    <Route path="logs/exceptions" element={<ExceptionLogs />} />
    <Route path="/admin/tenants/dashboard/logins" element={<LoginDashboard />} />
  </Route>
);

// In App.js:
//
// import { Routes } from 'react-router-dom';
// import { RootAdminRoutes } from './admin/routes';
//
// <Routes>
//   {/* ...your existing routes, AccountForm/LogIn/dashboards unchanged... */}
//   {RootAdminRoutes}
// </Routes>